import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import PrivacyBadge from "@/components/PrivacyBadge";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { GET_SEEN_TEMPLATE_ID, GET_SEEN_DURATIONS, GET_SEEN_RECOMMENDED_DAYS, buildGetSeenSteps } from "@/constants/getSeenTemplate";

// Standard Career Path duration options (in days). Filtered per plan bounds
// returned by GET /api/access → limits.career_path_duration_{min,max}.
const DURATION_OPTIONS = [
  { days: 90,   label_pl: "90 dni",   label_en: "90 days" },
  { days: 180,  label_pl: "6 miesięcy", label_en: "6 months" },
  { days: 365,  label_pl: "1 rok",    label_en: "1 year" },
  { days: 730,  label_pl: "2 lata",   label_en: "2 years" },
];

export default function GoalFormDialog({ open, onOpenChange, prefillTitle, onCreated, template }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);

  const [title, setTitle] = useState("");
  const [q1, setQ1] = useState("");         // q1_success (was Q1) — "Po czym poznaję"
  const [q5, setQ5] = useState("");         // q5_impact — "Wpływ na inne obszary"
  const [q4, setQ4] = useState("");         // q4_hours_per_week
  const [qCreating, setQCreating] = useState("");
  const [qPossibilities, setQPossibilities] = useState("");
  const [qHave, setQHave] = useState("");
  const [qMissing, setQMissing] = useState("");
  const [qStops, setQStops] = useState("");
  const [qLose, setQLose] = useState("");
  const [durationDays, setDurationDays] = useState(90);
  // Start with the most restrictive bounds (FREE plan) so the dropdown never
  // shows more options than the plan allows on first paint. /api/access
  // widens them a moment later if the user is on a paid plan.
  const [durationBounds, setDurationBounds] = useState({ min: 90, max: 90 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(prefillTitle || "");
      setQ1(""); setQ5(""); setQ4("");
      setQCreating(""); setQPossibilities(""); setQHave("");
      setQMissing(""); setQStops(""); setQLose("");
      setDurationDays(template === GET_SEEN_TEMPLATE_ID ? GET_SEEN_RECOMMENDED_DAYS : 90);
      // Pull plan bounds so the dropdown only shows durations this user can pick.
      api.get("/access").then((r) => {
        const min = r?.data?.limits?.career_path_duration_min ?? 90;
        const max = r?.data?.limits?.career_path_duration_max ?? 90;
        setDurationBounds({ min, max });
        // Clamp default into range.
        if (template !== GET_SEEN_TEMPLATE_ID) setDurationDays((d) => (d < min ? min : d > max ? max : d));
      }).catch(() => {});
    }
  }, [open, prefillTitle, template]);

  const isGetSeen = template === GET_SEEN_TEMPLATE_ID;
  const availableDurations = isGetSeen
    ? GET_SEEN_DURATIONS.filter((d) => d <= durationBounds.max).map((d) => ({
        days: d,
        label_pl: `${d} dni${d === GET_SEEN_RECOMMENDED_DAYS ? " (zalecane)" : ""}`,
        label_en: `${d} days${d === GET_SEEN_RECOMMENDED_DAYS ? " (recommended)" : ""}`,
      }))
    : DURATION_OPTIONS.filter(
        (o) => o.days >= durationBounds.min && o.days <= durationBounds.max,
      );

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) { toast.error(t("goal.save_fail")); return; }
    setSaving(true);
    try {
      const r = await api.post("/career/goals", {
        title: title.trim(),
        answers: {
          q_creating: qCreating,
          q1_success: q1,               // PO CZYM POZNAJĘ
          q_possibilities: qPossibilities,
          q5_impact: q5,                // JAK WPŁYWA NA INNE OBSZARY
          q_have: qHave,
          q_missing: qMissing,
          q_stops: qStops,
          q_lose: qLose,
          q4_hours_per_week: q4 ? parseFloat(q4) : null,
        },
        steps: isGetSeen ? buildGetSeenSteps(lang, durationDays) : [],
        duration_days: durationDays,
        template: template || null,
      });
      toast.success(t("goal.saved"));
      onOpenChange?.(false);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("goal.save_fail"));
    }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="goal-form-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        className="max-w-2xl bg-stone-950 border-stone-800 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="serif text-2xl text-stone-100">
              {t("plan.add_goal")}
            </DialogTitle>
            <PrivacyBadge />
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="label-cap">{t("plan.custom_goal")}</label>
            <input
              data-testid="goal-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("plan.custom_goal_ph")}
              className="script-input w-full"
              required
            />
          </div>

          <div>
            <label className="label-cap">1. {t("goal.q_creating")}</label>
            <p className="text-[11px] text-stone-500 mt-1 mb-1">{t("goal.q_creating_hint")}</p>
            <textarea data-testid="goal-q-creating" value={qCreating} onChange={(e) => setQCreating(e.target.value)}
              placeholder={t("goal.q_answer_ph")} rows={2}
              className="script-input w-full resize-none" />
          </div>
          <div>
            <label className="label-cap">2. {t("goal.q1")}</label>
            <p className="text-[11px] text-stone-500 mt-1 mb-1">{t("goal.q1_hint")}</p>
            <textarea data-testid="goal-q1" value={q1} onChange={(e) => setQ1(e.target.value)}
              placeholder={t("goal.q_answer_ph")} rows={2}
              className="script-input w-full resize-none" />
          </div>
          <div>
            <label className="label-cap">3. {t("goal.q_possibilities")}</label>
            <p className="text-[11px] text-stone-500 mt-1 mb-1">{t("goal.q_possibilities_hint")}</p>
            <textarea data-testid="goal-q-possibilities" value={qPossibilities} onChange={(e) => setQPossibilities(e.target.value)}
              placeholder={t("goal.q_answer_ph")} rows={2}
              className="script-input w-full resize-none" />
          </div>
          <div>
            <label className="label-cap">4. {t("goal.q5")}</label>
            <p className="text-[11px] text-stone-500 mt-1 mb-1">{t("goal.q5_hint")}</p>
            <textarea data-testid="goal-q5" value={q5} onChange={(e) => setQ5(e.target.value)}
              placeholder={t("goal.q_answer_ph")} rows={2}
              className="script-input w-full resize-none" />
          </div>
          <div>
            <label className="label-cap">5. {t("goal.q_have")}</label>
            <p className="text-[11px] text-stone-500 mt-1 mb-1">{t("goal.q_have_hint")}</p>
            <textarea data-testid="goal-q-have" value={qHave} onChange={(e) => setQHave(e.target.value)}
              placeholder={t("goal.q_answer_ph")} rows={2}
              className="script-input w-full resize-none" />
          </div>
          <div>
            <label className="label-cap">6. {t("goal.q_missing")}</label>
            <p className="text-[11px] text-stone-500 mt-1 mb-1">{t("goal.q_missing_hint")}</p>
            <textarea data-testid="goal-q-missing" value={qMissing} onChange={(e) => setQMissing(e.target.value)}
              placeholder={t("goal.q_answer_ph")} rows={2}
              className="script-input w-full resize-none" />
          </div>
          <div>
            <label className="label-cap">7. {t("goal.q_stops")}</label>
            <p className="text-[11px] text-stone-500 mt-1 mb-1">{t("goal.q_stops_hint")}</p>
            <textarea data-testid="goal-q-stops" value={qStops} onChange={(e) => setQStops(e.target.value)}
              placeholder={t("goal.q_answer_ph")} rows={2}
              className="script-input w-full resize-none" />
          </div>
          <div>
            <label className="label-cap">8. {t("goal.q_lose")}</label>
            <p className="text-[11px] text-stone-500 mt-1 mb-1">{t("goal.q_lose_hint")}</p>
            <textarea data-testid="goal-q-lose" value={qLose} onChange={(e) => setQLose(e.target.value)}
              placeholder={t("goal.q_answer_ph")} rows={2}
              className="script-input w-full resize-none" />
          </div>
          <div>
            <label className="label-cap">{t("goal.q4")}</label>
            <input data-testid="goal-q4" type="number" min="0" step="0.5"
              value={q4} onChange={(e) => setQ4(e.target.value)}
              className="script-input w-full" />
            <p className="text-[11px] text-stone-500 mt-1">{t("goal.q4_hint")}</p>
          </div>

          <div>
            <label className="label-cap">{lang === "en" ? "Career Path duration" : "Okres Career Path"}</label>
            <select
              data-testid="goal-duration"
              value={durationDays}
              onChange={(e) => setDurationDays(parseInt(e.target.value, 10))}
              className="script-input w-full"
              disabled={availableDurations.length <= 1}
            >
              {availableDurations.map((o) => (
                <option key={o.days} value={o.days}>
                  {lang === "en" ? o.label_en : o.label_pl}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-stone-500 mt-1">
              {lang === "en"
                ? `Your plan allows durations from ${isGetSeen ? 7 : durationBounds.min} to ${durationBounds.max} days.`
                : `Twój plan pozwala na okres od ${isGetSeen ? 7 : durationBounds.min} do ${durationBounds.max} dni.`}
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange?.(false)} className="ghost-btn">
              {t("action.cancel")}
            </button>
            <button type="submit" data-testid="goal-save" disabled={saving} className="pill-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {t("goal.save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
